import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TLang, changeLanguage, getLanguageFromStorage, getUserLanguage } from '@webtrader/core/i18n';
import { ELanguageQueryString, mapLangQueryStringToTLang } from '@webtrader/core/i18n/constants';
import { authService, useAuthWithKeys } from '@webtrader/core/services/auth';
import { useCompany } from '@webtrader/core/services/company';

import { IUseUpdateLanguageOnLoadParams } from './types';

const fallbackDefaultLanguage: TLang = 'en_GB';

export const useUpdateLanguageOnLoadWeb = (params: IUseUpdateLanguageOnLoadParams) => {
  const { languageQueryString } = params || {};
  const { company, langInitLoading, loading } = useCompany();
  const userLanguage = getUserLanguage();
  const { defaultLanguage = fallbackDefaultLanguage, supportedLanguages } = company || {};
  const { authenticated, ready } = useAuthWithKeys(['authenticated', 'ready']);

  const langQueryStringRef = useRef<ELanguageQueryString | undefined>(languageQueryString);
  const [isLangInitialised, setIsLangInitialised] = useState<boolean>(false);

  const selectedLanguage = useMemo(() => {
    const apiDefaultLanguage = defaultLanguage || fallbackDefaultLanguage;
    if (!langQueryStringRef.current || !supportedLanguages) {
      return apiDefaultLanguage;
    }
    const mapped = mapLangQueryStringToTLang[langQueryStringRef.current];
    return supportedLanguages.includes(mapped) ? mapped : apiDefaultLanguage;
  }, [defaultLanguage, supportedLanguages]);

  const handleChangeLanguage = useCallback(async () => {
    /**
     * WT-9721: Check if language has been stored in cookies (i.e. newly registered user)
     * If not, change to default language and store in cookies to prevent this from evaluating again when user changes language
     */
    const storedLanguage = getLanguageFromStorage();
    const isLangNotStored = !storedLanguage && selectedLanguage !== userLanguage;
    /**
     * WT-8792: Check if user has unsupported language when no query string present
     */
    const hasUnsupportedLanguage =
      !langQueryStringRef.current && supportedLanguages && userLanguage && !supportedLanguages.includes(userLanguage);
    /**
     * WT-8792: Check if query string language differs from user language
     */
    const hasLanguageQueryString = langQueryStringRef.current && selectedLanguage !== userLanguage;
    const shouldUpdateLanguage = isLangNotStored || hasUnsupportedLanguage || hasLanguageQueryString;
    if (shouldUpdateLanguage) {
      await changeLanguage({
        language: selectedLanguage,
        updateLanguageCallback: async () => {
          if (authenticated) {
            await authService.updateAccountLanguage();
          }
        }
      });
    }

    if (langQueryStringRef.current) {
      langQueryStringRef.current = undefined;
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [authenticated, selectedLanguage, userLanguage, supportedLanguages]);

  useEffect(() => {
    /**
     * WT-9721: Prevent handleChangeLanguage logic from evaluating before company data has been fully loaded
     * because handleChangeLanguage requires defaultLanguage and supportedLanguages fields
     */
    if (!ready || isLangInitialised || loading || !company || langInitLoading) {
      return;
    }
    /**
     * WT-8792: Added setIsLangInitialised state to track if handleChangeLanguage has been run
     * And disable if it has been run once
     *
     * i.e. if the language has been changed once alr, make sure that this is not run again if
     * user changes defaultLanguage/supportedLanguages (esp. if they change tab)
     */
    handleChangeLanguage().then(() => setIsLangInitialised(true));
  }, [handleChangeLanguage, ready, loading, company, isLangInitialised, langInitLoading]);
};

export const useUpdateLanguageOnLoadMobile = () => {
  const { company, langInitLoading, loading } = useCompany();
  const userLanguage = getUserLanguage();
  const { defaultLanguage, supportedLanguages } = company || {};
  const { authenticated, ready } = useAuthWithKeys(['authenticated', 'ready']);
  const [isLangInitialised, setIsLangInitialised] = useState<boolean>(false);

  const handleChangeLanguage = useCallback(async () => {
    const selectedLanguage = defaultLanguage ?? fallbackDefaultLanguage;
    /**
     * WT-9721: Check if language has been stored in cookies (i.e. newly registered user)
     * If not, change to default language and store in cookies to prevent this from evaluating again when user changes language
     */
    const storedLanguage = getLanguageFromStorage();
    const isLangNotStored = !storedLanguage && selectedLanguage !== userLanguage;
    const hasUnsupportedLanguage = supportedLanguages && userLanguage && !supportedLanguages.includes(userLanguage);
    const shouldUpdateLanguage = isLangNotStored || hasUnsupportedLanguage;

    if (shouldUpdateLanguage) {
      await changeLanguage({
        language: selectedLanguage,
        updateLanguageCallback: async () => {
          if (authenticated) {
            await authService.updateAccountLanguage();
          }
        }
      });
    }
  }, [authenticated, defaultLanguage, userLanguage, supportedLanguages]);

  useEffect(() => {
    /**
     * WT-9721: Prevent handleChangeLanguage logic from evaluating before company data has been fully loaded
     * because handleChangeLanguage requires defaultLanguage and supportedLanguages fields
     */
    if (!ready || isLangInitialised || loading || !company || langInitLoading) {
      return;
    }
    /**
     * WT-8792: Added setIsLangInitialised state to track if handleChangeLanguage has been run
     * And disable if it has been run once
     *
     * i.e. if the language has been changed once alr, make sure that this is not run again if
     * user changes defaultLanguage/supportedLanguages (esp. if they change tab)
     */
    handleChangeLanguage().then(() => setIsLangInitialised(true));
  }, [handleChangeLanguage, ready, loading, company, isLangInitialised, langInitLoading]);
};
